import React, { useEffect, useRef, useState } from 'react'

// components
import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'

// types
import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IEcommerceAnswerSchema,
  IEcommerceVideoAsset,
  IFileAsset
} from '@aytm/app/swidget/base/questions/Ecommerce/types'
import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'
import { getBigAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets.ts'

// custom hooks
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

// functions
import { assetDialogEvent } from '@aytm/app/swidget/base/questions/Ecommerce/utils/assetDialogEvent.ts'
import { getAssetType } from '@aytm/app/swidget/base/questions/Ecommerce/utils/getAssetType.ts'

// styles
import style from './AssetsModal.module.scss'

export interface IAssetsModelProps {
  answer: IEcommerceAnswerSchema
  orderedAssetsCollection: assetsCollectionElement[]
  assetsCollection: assetsCollectionElement[]
  index: number
  onAssetsModalClose: () => void
}
const AssetsModal = ({
  answer,
  orderedAssetsCollection,
  assetsCollection,
  index,
  onAssetsModalClose
}: IAssetsModelProps) => {
  const { t } = useSurveyLocale()
  // custom hooks calls
  const timeline = useTimeline()
  // refs
  const videoRef = useRef<HTMLVideoElement>(null)
  const playingIndexRef = useRef<number | null>(null)

  // state
  const [isManualSeek, setIsManualSeek] = useState<boolean>(false)
  const [selectedAsseteIndex, setSelectedAssetIndex] = useState<number>(index)
  const [videoTimecodes, setVideoTimecodes] = useState<{ [key: number]: number }>({})

  // functions
  const logVideoEvent = (type: 'play_video' | 'pause_video', assetIndex: number) =>
    timeline.log({
      type,
      ref: answer.id,
      target: orderedAssetsCollection.indexOf(assetsCollection[assetIndex]).toString(),
      context: ASSETS_TYPES.VIDEO.toLowerCase()
    })

  // The <video> unmounts on modal dismissal (and its src swaps in place on asset switch), so its own
  // pause event never reaches React. Ref-based and idempotent: a delivered pause clears the ref.
  const flushPlayingVideo = () => {
    if (null === playingIndexRef.current) return
    logVideoEvent('pause_video', playingIndexRef.current)
    playingIndexRef.current = null
  }

  const onTimeUpdate = () => {
    if (videoRef.current) {
      setVideoTimecodes(state => {
        return {
          ...state,
          [selectedAsseteIndex]: videoRef.current.currentTime
        }
      })
    }
  }
  const onAssetChange = (newIndex: number) => {
    if (videoRef.current && ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[selectedAsseteIndex])) {
      setVideoTimecodes(state => {
        return {
          ...state,
          [selectedAsseteIndex]: videoRef.current.currentTime
        }
      })
    }
    flushPlayingVideo()
    // Crossing a type boundary re-frames the dialog: close the outgoing type, open the incoming one
    // (same semantics as a tab switch in the Amazon / Walmart viewers). Same-type moves emit only show_asset.
    const next = assetsCollection[newIndex]
    if (getAssetType(assetsCollection[selectedAsseteIndex]) !== getAssetType(next)) {
      timeline.log({ ref: answer.id, type: assetDialogEvent('close', assetsCollection[selectedAsseteIndex]) })
      timeline.log({ ref: answer.id, type: assetDialogEvent('open', next) })
    }
    setSelectedAssetIndex(newIndex)
    setIsManualSeek(false)
  }

  const closeModal = () => {
    flushPlayingVideo()
    timeline.log({ ref: answer.id, type: assetDialogEvent('close', assetsCollection[selectedAsseteIndex]) })
    onAssetsModalClose()
  }
  const onVideoPlay = () => {
    playingIndexRef.current = selectedAsseteIndex
    logVideoEvent('play_video', selectedAsseteIndex)
  }
  const onVideoPause = () => {
    playingIndexRef.current = null
    logVideoEvent('pause_video', selectedAsseteIndex)
  }
  const onSeeking = () => {
    setIsManualSeek(true)
  }

  // effects
  useEffect(() => {
    if (!videoRef.current || ASSETS_TYPES.VIDEO !== getAssetType(assetsCollection[selectedAsseteIndex])) {
      return
    }
    const video = videoRef.current
    const newSrc = (assetsCollection[selectedAsseteIndex] as IEcommerceVideoAsset).video.url
    if (video.src !== newSrc) {
      video.src = newSrc
      video.load()
    }
    const handleCanPlay = () => {
      if (videoTimecodes[selectedAsseteIndex] && !isManualSeek) {
        video.currentTime = videoTimecodes[selectedAsseteIndex]
      }
      setIsManualSeek(false)
    }
    video.addEventListener('canplay', handleCanPlay, { once: true })
    return () => video.removeEventListener('canplay', handleCanPlay)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsseteIndex, assetsCollection])

  useEffect(() => {
    timeline.log({
      type: 'show_asset',
      ref: answer.id,
      target: orderedAssetsCollection.indexOf(assetsCollection[selectedAsseteIndex]).toString(),
      context: getAssetType(assetsCollection[selectedAsseteIndex]).toLowerCase()
    })
  }, [selectedAsseteIndex, answer.id, assetsCollection, orderedAssetsCollection, timeline])

  useEffect(
    () => () => flushPlayingVideo(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <div className={style.dialogLayout}>
      <button className={style.close} onClick={closeModal}>
        <i className="pi pi-times" />
      </button>
      <div className={style.mainImage}>
        {selectedAsseteIndex > 0 ? (
          <button className={style.arrowPrev} onClick={() => onAssetChange(selectedAsseteIndex - 1)}>
            <i className="pi pi-angle-left" />
          </button>
        ) : (
          <span />
        )}
        {ASSETS_TYPES.IMAGE === getAssetType(assetsCollection[selectedAsseteIndex]) && (
          <div
            className={style.image}
            role="img"
            aria-label={
              (assetsCollection[selectedAsseteIndex] as IImageAsset).alt_text || t('questions', 'img', 'noAlt')
            }
            style={{
              backgroundImage: `url(${getBigAssetPath(assetsCollection[selectedAsseteIndex] as IImageAsset)})`
            }}
          />
        )}
        {ASSETS_TYPES.MODEL === getAssetType(assetsCollection[selectedAsseteIndex]) && (
          <div className={style.modelView}>
            <model-viewer
              src={(assetsCollection[selectedAsseteIndex] as IFileAsset).file_url}
              camera-controls={false}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        )}
        {ASSETS_TYPES.VIDEO === getAssetType(assetsCollection[selectedAsseteIndex]) && (
          <video
            ref={videoRef}
            playsInline
            className={style.video}
            src={(assetsCollection[selectedAsseteIndex] as IEcommerceVideoAsset).video.url}
            preload="metadata"
            controls={true}
            onTimeUpdate={onTimeUpdate}
            onSeeking={onSeeking}
            onPlay={onVideoPlay}
            onPause={onVideoPause}
          />
        )}
        {selectedAsseteIndex !== assetsCollection.length - 1 ? (
          <button className={style.arrowNext} onClick={() => onAssetChange(selectedAsseteIndex + 1)}>
            <i className="pi pi-angle-right" />
          </button>
        ) : (
          <span />
        )}
      </div>
      <div className={style.collection}>
        {assetsCollection.map((asset, index) => (
          <div
            className={`${style.previewWrapper} ${index === selectedAsseteIndex ? style.previewWrapperActive : ''}`}
            key={index}
            onClick={() => onAssetChange(index)}
          >
            {ASSETS_TYPES.IMAGE === getAssetType(asset) && (
              <div
                key={index}
                className={style.preview}
                role="img"
                aria-label={(asset as IImageAsset).alt_text || t('questions', 'img', 'noAlt')}
                style={{
                  backgroundImage: `url(${getSmallAssetPath(asset as IImageAsset)})`
                }}
              />
            )}
            {ASSETS_TYPES.MODEL === getAssetType(asset) && (
              <div className={style.model}>
                <model-viewer
                  src={(asset as IFileAsset).file_url}
                  camera-controls={false}
                  interaction-prompt="none"
                  style={{ width: '62px', height: '62px' }}
                />
                <div className={style.modelOverlay} />
              </div>
            )}
            {ASSETS_TYPES.VIDEO === getAssetType(asset) && (
              <div
                className={style.videoPreview}
                role="presentation"
                style={{ backgroundImage: `url(${(asset as IEcommerceVideoAsset).video.thumbnail})` }}
              >
                <div className={style.playIcon} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default AssetsModal
